import React, { useEffect, useState } from 'react';
import { Edit2, X, Check } from 'lucide-react';
import { updateUser, saveAddress, updateUserAddress } from '../../../helpers/user';

const ProfileSection = ({ userData, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    postal_code: '',
    country: 'FR'
  });

  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        phone: userData.user_address?.phone || '',
        address_1: userData.user_address?.address_1 || '',
        address_2: userData.user_address?.address_2 || '',
        city: userData.user_address?.city || '',
        postal_code: userData.user_address?.postal_code || '',
        country: userData.user_address?.country || ''
      });
    }
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = {
      first_name: 'Le prénom est requis',
      last_name: 'Le nom est requis',
      phone: 'Le téléphone est requis',
      address_1: "L'adresse est requise",
      city: 'La ville est requise',
      postal_code: 'Le code postal est requis',
      country: 'Le pays est requis'
    };

    Object.entries(requiredFields).forEach(([field, message]) => {
      if (!formData[field]?.trim()) {
        errors[field] = message;
      }
    });

    // Phone number validation
    if (formData.phone && !/^[0-9+\s()-]{8,}$/.test(formData.phone)) {
      errors.phone = 'Numéro de téléphone invalide';
    }

    // Postal code validation for France
    if (formData.country.toUpperCase() === 'FR' && 
        formData.postal_code && 
        !/^\d{5}$/.test(formData.postal_code)) {
      errors.postal_code = 'Code postal invalide (5 chiffres)';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Veuillez remplir tous les champs requis correctement');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Update user profile
      const profileUpdateSuccess = await updateUser(userData.id, {
        first_name: formData.first_name,
        last_name: formData.last_name
      });

      if (!profileUpdateSuccess) {
        throw new Error('Erreur lors de la mise à jour du profil');
      }

      // Handle address update/creation
      const addressData = {
        data: {
          address_1: formData.address_1,
          address_2: formData.address_2,
          city: formData.city,
          postal_code: formData.postal_code,
          country: formData.country,
          phone: formData.phone
        }
      };

      if (userData.user_address?.id) {
        // Update existing address
        const addressUpdateSuccess = await updateUserAddress(
          userData.user_address.documentId, 
          addressData
        );
        if (!addressUpdateSuccess) {
          throw new Error('Erreur lors de l\'enregistrement de l\'adresse');
        }
      } else {
        // Create new address
        addressData.data.user = userData.id;
        const addressCreateSuccess = await saveAddress(addressData);
        if (!addressCreateSuccess) {
          throw new Error('Erreur lors de la création de l\'adresse');
        }
      }

      setSuccessMessage('Profil mis à jour avec succès');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      setError(error.message || 'Erreur lors de la mise à jour de l\'adresse');
    } finally {
      setLoading(false);
    }
  };

  const RequiredLabel = ({ children }) => (
    <label className="form-label">
      {children} <span className="text-danger">*</span>
    </label>
  );

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="card-title">Informations personnelles</h3>
          <button 
            className="btn btn-outline-primary" 
            onClick={() => {
              setIsEditing(!isEditing);
              setValidationErrors({});
              setError('');
            }}
          >
            {isEditing ? (
              <><X size={18} className="me-2" /> Annuler</>
            ) : (
              <><Edit2 size={18} className="me-2" /> Modifier</>
            )}
          </button>
        </div>

        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}
        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="row g-3">
            {/* Personal Information */}
            <div className="col-md-6">
              <RequiredLabel>Prénom</RequiredLabel>
              <input
                type="text"
                className={`form-control ${validationErrors.first_name ? 'is-invalid' : ''}`}
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
              {validationErrors.first_name && (
                <div className="invalid-feedback">{validationErrors.first_name}</div>
              )}
            </div>
            <div className="col-md-6">
              <RequiredLabel>Nom</RequiredLabel>
              <input
                type="text"
                className={`form-control ${validationErrors.last_name ? 'is-invalid' : ''}`}
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
              {validationErrors.last_name && (
                <div className="invalid-feedback">{validationErrors.last_name}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">E-mail</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={true}
              />
            </div>
            <div className="col-md-6">
              <RequiredLabel>Téléphone</RequiredLabel>
              <input
                type="tel"
                className={`form-control ${validationErrors.phone ? 'is-invalid' : ''}`}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
              {validationErrors.phone && (
                <div className="invalid-feedback">{validationErrors.phone}</div>
              )}
            </div>

            {/* Address Information */}
            <div className="col-12">
              <hr className="my-4" />
              <h4 className="mb-3">Adresse</h4>
            </div>
            
            <div className="col-12">
              <RequiredLabel>Adresse</RequiredLabel>
              <input
                type="text"
                className={`form-control ${validationErrors.address_1 ? 'is-invalid' : ''}`}
                name="address_1"
                value={formData.address_1}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
              {validationErrors.address_1 && (
                <div className="invalid-feedback">{validationErrors.address_1}</div>
              )}
            </div>
            <div className="col-12">
              <label className="form-label">Complément d'adresse</label>
              <input
                type="text"
                className="form-control"
                name="address_2"
                value={formData.address_2}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
            </div>
            <div className="col-md-6">
              <RequiredLabel>Ville</RequiredLabel>
              <input
                type="text"
                className={`form-control ${validationErrors.city ? 'is-invalid' : ''}`}
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
              {validationErrors.city && (
                <div className="invalid-feedback">{validationErrors.city}</div>
              )}
            </div>
            <div className="col-md-6">
              <RequiredLabel>Code postal</RequiredLabel>
              <input
                type="text"
                className={`form-control ${validationErrors.postal_code ? 'is-invalid' : ''}`}
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
              {validationErrors.postal_code && (
                <div className="invalid-feedback">{validationErrors.postal_code}</div>
              )}
            </div>
            <div className="col-md-6">
              <RequiredLabel>Pays</RequiredLabel>
              <input
                type="text"
                className={`form-control ${validationErrors.country ? 'is-invalid' : ''}`}
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
              {validationErrors.country && (
                <div className="invalid-feedback">{validationErrors.country}</div>
              )}
            </div>
          </div>
          
          {isEditing && (
            <div className="mt-4">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  <Check size={18} className="me-2" />
                )}
                Enregistrer les modifications
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ProfileSection;